import { useThrottle } from '@chronosphereio/shared';
import { ContentDimensions } from '@chronosphereio/viz-framework-core';
import { useEffect, useRef, useState } from 'react';

export type UseThrottledResizingOpts = {
  /**
   * How long to wait for new dimension changes before setting `isResizing` to
   * `false`. This option has priority over `throttleDimensionsMs` to ensure that
   * the latest dimensions are updated when `isResizing` is set to `false`.
   */
  isResizingTimeoutMs?: number;

  /**
   * How often to update the returned dimensions.
   *
   * If omitted in an environment with `requestAnimationFrame`, invocation will
   * be deferred until the next frame is drawn (typically about 16ms).
   *
   * Keep in mind the impact on the user when setting this value. Changes that
   * will be very visible to the user may feel janky if you set this value too
   * high and should use the default or a relatively low value (<100ms). Changes
   * that are very expensive to run may still have performance issues when using
   * lower values.
   */
  throttleDimensionsMs?: number;
};

export type UseThrottledResizingResponse = {
  /**
   * `true` when the component is actively resizing. Reverts to `false` after no
   * dimension changes are seen for `isResizingTimeoutMs`.
   */
  isResizing: boolean;

  /**
   * Width of the component throttled by `throttleDimensionsMs` to update less
   * frequently. Useful for reducing the performance impact of operations that
   * depend on the width of the component during frequent resizes.
   */
  throttledWidth: ContentDimensions['width'];

  /**
   * Height of the component throttled by `throttleDimensionsMs` to update less
   * frequently. Useful for reducing the performance impact of operations that
   * depend on the height of the component during frequent resizes.
   */
  throttledHeight: ContentDimensions['height'];
};

/**
 * Takes the dimensions of a component and returns information helpful for managing
 * resizing. Useful for managing the performance impact of certain operations
 * while a component is actively resizing.
 */
export function useThrottledResizing(
  dimensions: ContentDimensions,
  {
    // 200 is an arbitrary default that's a little higher than a user perceptible
    // wait (<100ms), but not too long. May need tuning over time.
    isResizingTimeoutMs = 200,

    // Default intentionally left `undefined`, which when passed to the
    // underlying `throttle` function will use `requestAnimationFrame`.
    throttleDimensionsMs,
  }: UseThrottledResizingOpts = {}
): UseThrottledResizingResponse {
  const prevDimensions = useRef<ContentDimensions>();
  const isResizingTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [isResizing, setIsResizing] = useState(false);
  const [throttledDimensions, setThrottledDimensions] = useState<ContentDimensions>(dimensions);

  const throttledSetThrottledDimensions = useThrottle(setThrottledDimensions, throttleDimensionsMs);

  function maybeClearTimeout() {
    if (isResizingTimeout.current) {
      clearTimeout(isResizingTimeout.current);
    }
  }

  useEffect(() => {
    return () => {
      // Ensure timeouts are cleared if the component unmounts.
      throttledSetThrottledDimensions.cancel();
      maybeClearTimeout();
    };
  }, [throttledSetThrottledDimensions]);

  useEffect(() => {
    if (prevDimensions.current === undefined) {
      // First load, set prev dimensions.
      prevDimensions.current = dimensions;
    } else if (
      prevDimensions.current.height !== dimensions.height ||
      prevDimensions.current.width !== dimensions.width
    ) {
      // Dimensions changed. Update them and start resizing handling.

      prevDimensions.current = dimensions;

      maybeClearTimeout();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsResizing(true);

      isResizingTimeout.current = setTimeout(() => {
        // Cancel throttled function, to cover edge cases where the throttle
        // might update the size after `isResizing` is set to `false`.
        throttledSetThrottledDimensions.cancel();

        // Explicitly update to the latest dimensions in case the last value
        // got missed because of throttling, so that the latest dimensions are
        // always returned at the point when `isResizing` is `false`.
        setThrottledDimensions(dimensions);
        setIsResizing(false);
      }, isResizingTimeoutMs);

      throttledSetThrottledDimensions(dimensions);
    }
  }, [dimensions, isResizingTimeoutMs, throttledSetThrottledDimensions]);

  return {
    isResizing,
    throttledWidth: throttledDimensions.width,
    throttledHeight: throttledDimensions.height,
  };
}
